import React, { useEffect, useMemo, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { UpdateStatus } from "services/game/type"

import { ReactComponent as Chevron } from "../../assets/icons/chevron-icon.svg"

import { formatAgo, formatDateTime, formatMs, safeTimeMs } from "./helpers/formatDates"

interface GamesUpdateStatusWidgetProps {
  status: UpdateStatus | null
  loading: boolean
  now: number
  cooldownLeftMs: number
  onRefresh: () => Promise<void>
}

export function GamesUpdateStatusWidget({ status, loading, now, cooldownLeftMs, onRefresh }: GamesUpdateStatusWidgetProps) {
  const { t } = useTranslation("translation", { useSuspense: false })
  const inProgress = !!status?.inProgress
  const allowed = !!status?.allowed
  const nextAllowedAt = status?.nextAllowedAt ?? null

  const startedAt = status?.startedAt ?? null
  const startedAtMs = useMemo(() => safeTimeMs(startedAt), [startedAt])

  const runningForMs = useMemo(() => {
    if (!inProgress) return 0
    if (!startedAtMs) return 0
    return Math.max(0, now - startedAtMs)
  }, [inProgress, startedAtMs, now])

  const showRunningMeta = inProgress && startedAtMs > 0

  const [open, setOpen] = useState<boolean>(true)

  useEffect(() => {
    if (inProgress) setOpen(true)
  }, [inProgress])

  return (
    <div
      className={clsx(
        "rounded-xl mt-3 border bg-white dark:bg-[#0C0C1A] p-4 transition-all",
        "border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]",
        !open &&
          "cursor-pointer hover:bg-[rgba(132,_0,_168,_0.03)] dark:hover:bg-[rgba(132,_0,_168,_0.08)] hover:brightness-[1.03] dark:hover:brightness-[1.06]"
      )}
      onClick={() => {
        if (!open) setOpen(true)
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <div className="text-sm font-semibold text-[rgba(6,7,21,1)] dark:text-white">{t("gameStatus.title")}</div>
          <div className="text-xs text-[rgba(6,7,21,0.65)] dark:text-[rgba(255,255,255,0.65)]">{t("gameStatus.liveStatus")}</div>

          {showRunningMeta ? (
            <div className="mt-1 text-xs text-[rgba(6,7,21,0.65)] dark:text-[rgba(255,255,255,0.65)]">
              {t("gameStatus.started")} <b>{formatDateTime(startedAt)}</b> <span className="opacity-80">•</span>{" "}
              {t("gameStatus.runningFor")} <b>{formatMs(runningForMs)}</b>
            </div>
          ) : inProgress ? (
            <div className="mt-1 text-xs text-[rgba(6,7,21,0.65)] dark:text-[rgba(255,255,255,0.65)]">
              {t("gameStatus.runningEllipsis")} <span className="opacity-70">{t("gameStatus.startedAtNotProvided")}</span>
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <StatusPill ok={!inProgress}>{inProgress ? t("gameStatus.running") : t("gameStatus.idle")}</StatusPill>

          <button
            type="button"
            className={clsx(
              "btn btn-ghost btn-sm",
              "border border-[rgba(132,_0,_168,_0.25)] text-black dark:text-white",
              "hover:bg-[rgba(132,_0,_168,_0.06)] dark:hover:bg-[rgba(132,_0,_168,_0.10)]"
            )}
            onClick={onRefresh}
            disabled={loading}
            title={t("gameStatus.refresh") as string}
          >
            {loading ? t("gameStatus.refreshing") : t("gameStatus.refresh")}
          </button>

          <button
            type="button"
            className={clsx("btn btn-ghost btn-sm", "hover:bg-[rgba(132,_0,_168,_0.06)] dark:hover:bg-[rgba(132,_0,_168,_0.10)]")}
            onClick={() => setOpen((v) => !v)}
          >
            <Chevron
              className={clsx("[&>path]:stroke-[rgba(6,7,21,1)] transition-all dark:[&>path]:stroke-white", open ? "rotate-180" : "")}
            />
          </button>
        </div>
      </div>

      {open ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-lg border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.08)] p-3">
            <div className="text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">{t("gameStatus.manualRun")}</div>
            <div className="mt-1 text-sm font-medium text-[rgba(6,7,21,1)] dark:text-white">
              {formatDateTime(status?.lastManualRunAt ?? null)}
            </div>
            <div className="mt-1 text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">
              {status?.lastManualRunAt ? formatAgo(now, status.lastManualRunAt) : "—"}
            </div>
            {status?.lastManualTriggeredByUserId ? (
              <div className="mt-1 text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">
                {t("gameStatus.byUser")} <span className="font-mono">{status.lastManualTriggeredByUserId}</span>
              </div>
            ) : null}
          </div>

          <div className="rounded-lg border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.08)] p-3">
            <div className="text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">{t("gameStatus.cronRun")}</div>
            <div className="mt-1 text-sm font-medium text-[rgba(6,7,21,1)] dark:text-white">
              {formatDateTime(status?.lastCronRunAt ?? null)}
            </div>
            <div className="mt-1 text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">
              {status?.lastCronRunAt ? formatAgo(now, status.lastCronRunAt) : "—"}
            </div>
          </div>

          <div className="rounded-lg border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.08)] p-3">
            <div className="text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">{t("gameStatus.availability")}</div>
            <div className="mt-1 text-sm font-medium text-[rgba(6,7,21,1)] dark:text-white">
              {allowed ? t("gameStatus.allowed") : t("gameStatus.coolingDown")}
            </div>
            <div className="mt-1 text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">
              {!allowed && nextAllowedAt ? `In ${formatMs(cooldownLeftMs)}` : allowed ? t("gameStatus.canTriggerNow") : "—"}
            </div>
          </div>

          <div className="rounded-lg border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.08)] p-3">
            <div className="text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">{t("gameStatus.nextAllowedAt")}</div>
            <div className="mt-1 text-sm font-medium text-[rgba(6,7,21,1)] dark:text-white">{formatDateTime(nextAllowedAt)}</div>
            <div className="mt-1 text-xs text-[rgba(6,7,21,0.6)] dark:text-[rgba(255,255,255,0.6)]">
              {nextAllowedAt ? formatAgo(now, nextAllowedAt) : "—"}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function StatusPill({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium border",
        ok
          ? "bg-[rgba(16,185,129,0.08)] text-[rgba(16,185,129,1)] border-[rgba(16,185,129,0.25)]"
          : "bg-[rgba(245,158,11,0.08)] text-[rgba(245,158,11,1)] border-[rgba(245,158,11,0.25)]"
      )}
    >
      <span className={clsx("h-2 w-2 rounded-full", ok ? "bg-[rgba(16,185,129,1)]" : "bg-[rgba(245,158,11,1)]")} />
      {children}
    </span>
  )
}
